<template>
  <div class="tab-content">
    <div class="main-header">
      <div>
        <h1>{{ $t('controlCenter.tickets.title') }}</h1>
        <p class="subtitle">{{ $t('controlCenter.tickets.subtitle') }}</p>
      </div>
      <button class="btn btn-primary" @click="openCreateDialog">
        <i class="fas fa-plus"></i>
        {{ $t('controlCenter.tickets.create') }}
      </button>
    </div>

    <div class="card">
      <div class="card-header filter-row">
        <el-input
          v-model.trim="filters.q"
          :placeholder="$t('controlCenter.tickets.searchPlaceholder')"
          clearable
          class="keyword-input"
          @clear="fetchTickets"
          @keyup.enter.native="fetchTickets"
        ></el-input>
        <button class="btn btn-primary" @click="fetchTickets">
          <i class="fas fa-magnifying-glass"></i>
          {{ $t('controlCenter.common.search') }}
        </button>
        <button class="btn btn-sm" @click="resetFilters">
          {{ $t('controlCenter.common.reset') }}
        </button>
      </div>

      <div class="card-body">
        <el-table :data="tickets" v-loading="loading" style="width: 100%" :empty-text="$t('controlCenter.tickets.empty')">
          <el-table-column :label="$t('controlCenter.tickets.columns.id')" min-width="220">
            <template slot-scope="scope">
              <span class="ticket-id">{{ formatId(scope.row.id) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="title" :label="$t('controlCenter.tickets.columns.title')" min-width="220"></el-table-column>
          <el-table-column :label="$t('controlCenter.tickets.columns.category')" min-width="140">
            <template slot-scope="scope">
              {{ categoryLabel(scope.row.category) }}
            </template>
          </el-table-column>
          <el-table-column :label="$t('controlCenter.common.status')" min-width="120">
            <template slot-scope="scope">
              <el-tag size="small" :type="ticketStatusTagType(scope.row.status)">
                {{ ticketStatusLabel(scope.row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column :label="$t('controlCenter.tickets.columns.createdBy')" min-width="220">
            <template slot-scope="scope">
              <span class="ticket-id">{{ formatId(scope.row.createdBy) }}</span>
            </template>
          </el-table-column>
          <el-table-column :label="$t('controlCenter.tickets.columns.createdAt')" min-width="180">
            <template slot-scope="scope">
              {{ formatInstant(scope.row.createdAt) }}
            </template>
          </el-table-column>
          <el-table-column :label="$t('controlCenter.common.actions')" min-width="220">
            <template slot-scope="scope">
              <div class="action-buttons">
                <button class="btn btn-sm" @click="openDetail(scope.row)">
                  {{ $t('controlCenter.common.viewDetails') }}
                </button>
                <button
                  v-if="scope.row.status !== 'CLOSED'"
                  class="btn btn-sm"
                  @click="closeTicket(scope.row)"
                >
                  {{ $t('controlCenter.tickets.closeTicket') }}
                </button>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div v-if="totalElements > 0" class="pagination-wrap">
        <el-pagination
          :current-page="pageNumber + 1"
          :page-size="pageSize"
          :page-sizes="[10, 20, 50]"
          :total="totalElements"
          layout="total, sizes, prev, pager, next"
          @current-change="handlePageChange"
          @size-change="handleSizeChange"
        ></el-pagination>
      </div>
    </div>

    <el-dialog
      :title="$t('controlCenter.tickets.createTitle')"
      :visible.sync="createDialogVisible"
      width="560px"
    >
      <el-form ref="ticketForm" label-position="top" :model="form" :rules="formRules">
        <el-form-item :label="$t('controlCenter.tickets.columns.title')" prop="title">
          <el-input v-model.trim="form.title" :placeholder="$t('controlCenter.tickets.titlePlaceholder')"></el-input>
        </el-form-item>
        <el-form-item :label="$t('controlCenter.tickets.columns.category')" prop="category">
          <el-select v-model="form.category" class="full-width" :placeholder="$t('controlCenter.tickets.categoryPlaceholder')">
            <el-option
              v-for="item in categoryOptions"
              :key="item"
              :label="categoryLabel(item)"
              :value="item"
            ></el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="createDialogVisible = false">{{ $t('controlCenter.common.cancel') }}</el-button>
        <el-button type="primary" :loading="saving" @click="createTicket">
          {{ $t('controlCenter.common.save') }}
        </el-button>
      </span>
    </el-dialog>

    <el-dialog
      :title="$t('controlCenter.tickets.detailTitle')"
      :visible.sync="detailDialogVisible"
      width="900px"
      custom-class="ticket-detail-dialog"
    >
      <div v-loading="detailLoading">
        <div v-if="selectedTicket" class="ticket-detail-layout">
          <div class="detail-grid">
            <div class="detail-item detail-span-2">
              <span>{{ $t('controlCenter.tickets.columns.title') }}</span>
              <strong>{{ selectedTicket.title || '-' }}</strong>
            </div>
            <div class="detail-item">
              <span>{{ $t('controlCenter.tickets.columns.id') }}</span>
              <strong>{{ formatId(selectedTicket.id) }}</strong>
            </div>
            <div class="detail-item">
              <span>{{ $t('controlCenter.common.status') }}</span>
              <strong>{{ ticketStatusLabel(selectedTicket.status) }}</strong>
            </div>
            <div class="detail-item">
              <span>{{ $t('controlCenter.tickets.columns.category') }}</span>
              <strong>{{ categoryLabel(selectedTicket.category) }}</strong>
            </div>
            <div class="detail-item">
              <span>{{ $t('controlCenter.tickets.columns.createdAt') }}</span>
              <strong>{{ formatInstant(selectedTicket.createdAt) }}</strong>
            </div>
          </div>

          <div class="card detail-card">
            <div class="card-header">
              <h3 class="card-title">{{ $t('controlCenter.tickets.messages') }}</h3>
            </div>
            <div class="card-body">
              <div v-if="ticketMessages.length" class="message-list">
                <div
                  v-for="message in ticketMessages"
                  :key="messageKey(message)"
                  class="message-item"
                  :class="message.authorType === 'EMPLOYEE' ? 'employee-message' : 'customer-message'"
                >
                  <div class="message-meta">
                    <el-tag size="mini" :type="message.authorType === 'EMPLOYEE' ? 'success' : 'info'">
                      {{ authorTypeLabel(message.authorType) }}
                    </el-tag>
                    <span>{{ formatInstant(message.sentAt) }}</span>
                    <span>{{ formatId(message.sender) }}</span>
                  </div>
                  <p>{{ message.content }}</p>
                </div>
              </div>
              <div v-else class="empty-state">
                {{ $t('controlCenter.tickets.noMessages') }}
              </div>
            </div>
          </div>

          <div v-if="selectedTicket.status !== 'CLOSED'" class="reply-box">
            <el-input
              v-model.trim="replyContent"
              type="textarea"
              :rows="4"
              :placeholder="$t('controlCenter.tickets.replyPlaceholder')"
            ></el-input>
            <div class="reply-actions">
              <button class="btn btn-primary" :disabled="replySaving" @click="addMessage">
                <i class="fas fa-paper-plane"></i>
                {{ $t('controlCenter.tickets.sendMessage') }}
              </button>
              <button class="btn btn-sm" :disabled="replySaving" @click="closeTicket(selectedTicket)">
                {{ $t('controlCenter.tickets.closeTicket') }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="detailDialogVisible = false">{{ $t('controlCenter.common.close') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { ticketApi } from '@/api/ticket'

export default {
  name: 'TicketManagement',
  data() {
    return {
      loading: false,
      saving: false,
      detailLoading: false,
      replySaving: false,
      createDialogVisible: false,
      detailDialogVisible: false,
      tickets: [],
      selectedTicket: null,
      replyContent: '',
      totalElements: 0,
      pageNumber: 0,
      pageSize: 10,
      filters: {
        q: ''
      },
      form: {
        title: '',
        category: ''
      },
      categoryOptions: ['REFUND', 'LICENSE', 'LOGIN', 'AUDIT', 'OTHER']
    }
  },
  computed: {
    formRules() {
      return {
        title: [
          { required: true, message: this.$t('controlCenter.tickets.titleRequired'), trigger: 'blur' }
        ],
        category: [
          { required: true, message: this.$t('controlCenter.tickets.categoryRequired'), trigger: 'change' }
        ]
      }
    },
    ticketMessages() {
      return this.selectedTicket && Array.isArray(this.selectedTicket.messages)
        ? this.selectedTicket.messages
        : []
    }
  },
  created() {
    this.fetchTickets()
  },
  methods: {
    async fetchTickets() {
      this.loading = true
      try {
        const response = await ticketApi.getTickets({
          q: this.filters.q || undefined,
          page: this.pageNumber,
          size: this.pageSize
        })
        const data = response && response.data ? response.data : response
        this.tickets = Array.isArray(data) ? data : data.content || []
        this.totalElements = data.totalElements || data.total || this.tickets.length
        this.pageNumber = typeof data.number === 'number' ? data.number : this.pageNumber
        this.pageSize = typeof data.size === 'number' ? data.size : this.pageSize
      } catch (error) {
        console.error('Failed to fetch tickets:', error)
        this.$message.error(this.$t('controlCenter.tickets.loadError'))
      } finally {
        this.loading = false
      }
    },
    resetFilters() {
      this.filters.q = ''
      this.pageNumber = 0
      this.fetchTickets()
    },
    openCreateDialog() {
      this.form = {
        title: '',
        category: ''
      }
      this.createDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.ticketForm) {
          this.$refs.ticketForm.clearValidate()
        }
      })
    },
    createTicket() {
      this.$refs.ticketForm.validate(async valid => {
        if (!valid) return
        this.saving = true
        try {
          const response = await ticketApi.createTicket({
            title: this.form.title,
            category: this.form.category
          })
          const ticket = response && response.data ? response.data : response
          this.$message.success(this.$t('controlCenter.tickets.createSuccess'))
          this.createDialogVisible = false
          await this.fetchTickets()
          if (ticket && ticket.id) {
            await this.openDetail(ticket)
          }
        } catch (error) {
          console.error('Failed to create ticket:', error)
          this.$message.error(this.$t('controlCenter.tickets.createError'))
        } finally {
          this.saving = false
        }
      })
    },
    async openDetail(ticket) {
      const id = this.resolveTicketId(ticket)
      if (!id) {
        this.$message.error(this.$t('controlCenter.tickets.missingId'))
        return
      }
      this.detailDialogVisible = true
      this.detailLoading = true
      this.replyContent = ''
      try {
        const response = await ticketApi.getTicket(id)
        this.selectedTicket = response && response.data ? response.data : response
      } catch (error) {
        console.error('Failed to fetch ticket detail:', error)
        this.$message.error(this.$t('controlCenter.tickets.detailError'))
      } finally {
        this.detailLoading = false
      }
    },
    async addMessage() {
      if (!this.replyContent) {
        this.$message.warning(this.$t('controlCenter.tickets.replyRequired'))
        return
      }
      const id = this.resolveTicketId(this.selectedTicket)
      if (!id) {
        this.$message.error(this.$t('controlCenter.tickets.missingId'))
        return
      }
      this.replySaving = true
      try {
        await ticketApi.addMessage(id, { content: this.replyContent })
        this.$message.success(this.$t('controlCenter.tickets.replySuccess'))
        this.replyContent = ''
        await this.openDetail(this.selectedTicket)
        await this.fetchTickets()
      } catch (error) {
        console.error('Failed to add ticket message:', error)
        this.$message.error(this.$t('controlCenter.tickets.replyError'))
      } finally {
        this.replySaving = false
      }
    },
    async closeTicket(ticket) {
      const id = this.resolveTicketId(ticket)
      if (!id) {
        this.$message.error(this.$t('controlCenter.tickets.missingId'))
        return
      }
      try {
        await this.$confirm(
          this.$t('controlCenter.tickets.closeConfirmMessage'),
          this.$t('controlCenter.tickets.closeConfirmTitle'),
          {
            confirmButtonText: this.$t('controlCenter.tickets.closeTicket'),
            cancelButtonText: this.$t('controlCenter.common.cancel'),
            type: 'warning'
          }
        )
      } catch (error) {
        return
      }
      try {
        await ticketApi.closeTicket(id)
        this.$message.success(this.$t('controlCenter.tickets.closeSuccess'))
        if (this.selectedTicket && this.resolveTicketId(this.selectedTicket) === id) {
          await this.openDetail(this.selectedTicket)
        }
        await this.fetchTickets()
      } catch (error) {
        console.error('Failed to close ticket:', error)
        this.$message.error(this.$t('controlCenter.tickets.closeError'))
      }
    },
    handlePageChange(page) {
      this.pageNumber = page - 1
      this.fetchTickets()
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.pageNumber = 0
      this.fetchTickets()
    },
    resolveTicketId(ticket) {
      return this.formatId(ticket && ticket.id)
    },
    formatId(value) {
      if (!value) return ''
      if (typeof value === 'string') return value
      if (typeof value === 'number') return String(value)
      if (value.uuid) return value.uuid
      if (value.value) return value.value
      if (value.id) return value.id
      if (typeof value.mostSigBits !== 'undefined' && typeof value.leastSigBits !== 'undefined') {
        return this.uuidFromBits(value.mostSigBits, value.leastSigBits)
      }
      return JSON.stringify(value)
    },
    uuidFromBits(mostSigBits, leastSigBits) {
      if (typeof BigInt === 'undefined') {
        return ''
      }
      try {
        const mostHex = this.toUnsignedHex(mostSigBits, 16)
        const leastHex = this.toUnsignedHex(leastSigBits, 16)
        const hex = `${mostHex}${leastHex}`
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
      } catch (error) {
        return ''
      }
    },
    toUnsignedHex(value, length) {
      const one = BigInt(1)
      const width = one << BigInt(length * 4)
      let parsed = BigInt(value)
      if (parsed < BigInt(0)) {
        parsed += width
      }
      return parsed.toString(16).padStart(length, '0').slice(-length)
    },
    formatInstant(value) {
      if (!value) return '-'
      if (typeof value === 'string') {
        return value.replace('T', ' ').replace('Z', '').slice(0, 19)
      }
      if (typeof value === 'number') {
        return new Date(value).toLocaleString()
      }
      if (typeof value.seconds !== 'undefined') {
        return new Date(Number(value.seconds) * 1000).toLocaleString()
      }
      return '-'
    },
    categoryLabel(category) {
      const key = `controlCenter.ticketCategories.${category || 'OTHER'}`
      const text = this.$t(key)
      return text === key ? category || '-' : text
    },
    ticketStatusLabel(status) {
      const key = `controlCenter.ticketStatusLabels.${status || 'UNKNOWN'}`
      const text = this.$t(key)
      return text === key ? status || this.$t('controlCenter.common.unknown') : text
    },
    ticketStatusTagType(status) {
      if (status === 'OPEN') return 'success'
      if (status === 'CLOSED') return 'info'
      return 'warning'
    },
    authorTypeLabel(authorType) {
      const key = `controlCenter.ticketAuthorTypes.${authorType || 'UNKNOWN'}`
      const text = this.$t(key)
      return text === key ? authorType || this.$t('controlCenter.common.unknown') : text
    },
    messageKey(message) {
      return this.formatId(message.id) || `${this.formatInstant(message.sentAt)}-${message.content}`
    }
  }
}
</script>

<style scoped>
.filter-row {
  gap: 12px;
  flex-wrap: wrap;
}

.keyword-input {
  width: 320px;
}

.full-width {
  width: 100%;
}

.pagination-wrap {
  display: flex;
  justify-content: flex-end;
  padding: 0 24px 24px;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.ticket-id {
  font-family: Monaco, Consolas, monospace;
  font-size: 12px;
  color: #475569;
  word-break: break-all;
}

.ticket-detail-layout {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.detail-card {
  margin-bottom: 0;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.detail-item span {
  color: #64748b;
  font-size: 12px;
}

.detail-item strong {
  color: #0f172a;
  font-size: 14px;
  word-break: break-word;
}

.detail-span-2 {
  grid-column: span 2;
}

.message-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.message-item {
  padding: 14px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 8px;
  background: #f8fafc;
}

.message-item.employee-message {
  background: #f0fdf4;
  border-color: rgba(34, 197, 94, 0.2);
}

.message-item.customer-message {
  background: #f8fafc;
}

.message-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
  color: #64748b;
  font-size: 12px;
}

.message-item p {
  margin: 0;
  color: #0f172a;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
}

.empty-state {
  padding: 18px;
  text-align: center;
  color: #64748b;
  background: #f8fafc;
  border-radius: 8px;
}

.reply-box {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.reply-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.btn[disabled] {
  cursor: not-allowed;
  opacity: 0.65;
}

@media screen and (max-width: 768px) {
  .keyword-input {
    width: 100%;
  }

  .pagination-wrap {
    justify-content: center;
    padding: 0 12px 18px;
  }

  .action-buttons,
  .reply-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .action-buttons .el-button,
  .reply-actions .btn {
    width: 100%;
    justify-content: center;
    margin-left: 0;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .detail-span-2 {
    grid-column: span 1;
  }
}
</style>
